import { useMinOrderQtyValidator } from '@/composable/useMinOrderQtyValidator';
import UserError from '@/error';
import i18n from '@/i18n';
import Cart from '@/interfaces/Cart';
import CartMenu from '@/interfaces/CartMenu';
import Topping from '@/interfaces/Topping';
import { useConfigStore } from '@/stores/config';
import { useMenuStore } from '@/stores/menu';
import { getMenuTaxAmount } from '@/utils';
import { useStorage } from '@vueuse/core';
import equal from 'deep-equal';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useCartStore = defineStore('cart', () => {
  const configStore = useConfigStore();
  const menuStore = useMenuStore();

  const cart = useStorage('CART', { CartMenus: [] } as Cart);

  const { adjustMinOrderQtyAsync } = useMinOrderQtyValidator();

  const resolveQtyPromise = ref();
  const selectedOption = ref<Topping>();
  const selectedOptionGroupName = ref('');
  const selectedOptionMaxQty = ref(1);
  const isQtyModalShown = ref(false);

  // 장바구니 메뉴 총 수량
  const totalQty = computed(() => {
    return cart.value.CartMenus.reduce((acc, cartMenu) => acc + cartMenu.Qty, 0);
  });

  // 장바구니 메뉴 가지수
  const totalMenuTypeQty = computed(() => {
    return cart.value.CartMenus.length || 0;
  });

  // 장바구니 총 금액 (세금 옵션에 따라 세금이 포함되어 있지 않은 금액)
  const totalAmount = computed(() => {
    return cart.value.CartMenus.reduce(
      (acc, cartMenu) => acc + getCartMenuPrice(cartMenu, cartMenu.Qty, configStore.config.IsTaxIncluded),
      0,
    );
  });

  // 장바구니 총 결제 금액 (세금 포함)
  const finalAmount = computed(() => {
    return cart.value.CartMenus.reduce((acc, cartMenu) => acc + getCartMenuPrice(cartMenu, cartMenu.Qty, true), 0);
  });

  /**
   * 장바구니 데이터에 인원 수 저장
   * @param headcount 인원 수
   */
  const setHeadcount = (headcount?: number) => {
    cart.value.Headcount = headcount;
  };

  /**
   * 장바구니 메뉴 가격 조회
   * @param menu 메뉴 데이터
   * @param addPrice 토핑 또는 세트메뉴 추가 금액
   * @param isTaxIncluded 세금 별도인 경우 세금 포함 여부
   * @returns 메뉴 가격
   */
  const getCartMenuPrice = (cartMenu: CartMenu, qty: number, isTaxIncluded: boolean) => {
    let addPrice = 0;
    const menu = menuStore.getMenu(cartMenu.GroupCode, cartMenu.ItemCode);

    if (cartMenu.Toppings && cartMenu.Toppings.length > 0) {
      addPrice += cartMenu.Toppings.reduce((acc, cartTopping) => {
        const topping = menuStore.getTopping(cartTopping.GroupCode.toString(), cartTopping.OptionCode);
        return acc + menuStore.getToppingPrice(topping, menu.IsTax2) * cartTopping.Qty;
      }, 0);
    }

    if (cartMenu.SetMenus && cartMenu.SetMenus.length > 0) {
      addPrice += cartMenu.SetMenus.reduce((acc, cartSetMenu) => {
        const setMenu = menuStore.getSetMenu(
          cartSetMenu.MenuItemCode,
          Number(cartSetMenu.GroupCode),
          cartSetMenu.OptionCode,
        );
        return acc + menuStore.getSetMenuPrice(setMenu, menu.IsTax2) * cartSetMenu.Qty;
      }, 0);
    }

    return menuStore.getMenuAmount(menu.FinalPrice, addPrice, qty, isTaxIncluded, menu.IsTax, menu.IsTax2);
  };

  /**
   * 장바구니에 메뉴 추가. 같은 메뉴인 경우 수량 증가
   * @param addCartMenu 장바구니 메뉴 데이터
   */
  const addCartMenu = async (_cartMenu: CartMenu): Promise<boolean> => {
    // 장바구니 추가 완료 여부
    let isAdded = false;

    // 최소주문수량 유효성 검사 및 수량 조정
    const isSuccess = await adjustMinOrderQtyAsync(_cartMenu);
    if (!isSuccess) return false;

    cart.value.CartMenus.forEach((cartMenu) => {
      // 수량 제외 후 같은지 비교
      const cloneCartMenu: any = Object.assign({}, cartMenu);
      const cloneAddedeCartMenu: any = Object.assign({}, _cartMenu);
      delete cloneCartMenu['Qty'];
      delete cloneAddedeCartMenu['Qty'];

      // 장바구니에 토핑, 세트메뉴까지 완전히 같은 메뉴가 있는 경우
      if (equal(cloneCartMenu, cloneAddedeCartMenu)) {
        // 최대 수량 체크
        if (cartMenu.Qty + _cartMenu.Qty > 99) throw new UserError(i18n.global.t('error.exceed_maximum'));
        // 수량 업데이트
        cartMenu.Qty = cartMenu.Qty + _cartMenu.Qty;
        isAdded = true;
        return;
      }
    });

    // 장바구니에 완전히 같은 메뉴가 없을 경우 신규 추가
    if (!isAdded) cart.value.CartMenus.push(_cartMenu);
    return true;
  };

  /**
   * 장바구니에서 메뉴 삭제
   * @param cartMenu 삭제할 장바구니 메뉴
   */
  const removeCartMenu = (cartMenu: CartMenu) => {
    cart.value.CartMenus.splice(cart.value.CartMenus.indexOf(cartMenu), 1);
  };

  /** 장바구니 전체 초기화 */
  const initializeCart = () => {
    cart.value = { CartMenus: [] } as Cart;
  };

  /**
   * 장바구니 메뉴 수량 변경
   * @param index 장바구니 메뉴 순번
   * @param qty 변경할 수량
   */
  const setQty = (index: number, qty: number) => {
    cart.value.CartMenus[index].Qty = qty;
  };

  const showQtyModal = (option: Topping, groupName: string, maxQty: number): Promise<number> => {
    selectedOption.value = option;
    selectedOptionGroupName.value = groupName;
    selectedOptionMaxQty.value = maxQty;
    isQtyModalShown.value = true;

    return new Promise((resolve, _) => {
      resolveQtyPromise.value = resolve;
    });
  };

  const hideQtyModal = () => {
    isQtyModalShown.value = false;
  };

  /**
   * 메뉴별 장바구니 수량 조회
   * @param groupCode 메뉴 그룹 코드
   * @param itemCode 메뉴 아이템 코드
   * @returns 메뉴별 장바구니 수량
   */
  const getQtyByMenu = (groupCode: string, itemCode: string): number => {
    return cart.value.CartMenus.filter(
      (cartMenu) => cartMenu.GroupCode === groupCode && cartMenu.ItemCode === itemCode,
    ).reduce((acc, cartMenu) => acc + cartMenu.Qty, 0);
  };

  /** 장바구니 메뉴 세금 조회 */
  const getCartTotalTaxAmount = (): number => {
    return (
      cart.value.CartMenus.reduce((acc, cartMenu) => {
        const menu = menuStore.getMenu(cartMenu.GroupCode, cartMenu.ItemCode);
        return acc + getMenuTaxAmount(getCartMenuPrice(cartMenu, cartMenu.Qty, true), menu.IsTax, menu.IsTax2);
      }, 0) || 0
    );
  };

  /** 장바구니 메뉴 면세금액 조회 */
  const getCartTotalFreeAmount = (): number => {
    return cart.value.CartMenus.reduce((acc, cartMenu) => {
      const menu = menuStore.getMenu(cartMenu.GroupCode, cartMenu.ItemCode);
      const menuTotalPrice = getCartMenuPrice(cartMenu, cartMenu.Qty, true);
      const menuTaxAmount = getMenuTaxAmount(menuTotalPrice, menu.IsTax, menu.IsTax2);
      return acc + (menuTaxAmount === 0 ? menuTotalPrice : 0);
    }, 0);
  };

  return {
    cart,
    resolveQtyPromise,
    isQtyModalShown,
    selectedOption,
    selectedOptionGroupName,
    selectedOptionMaxQty,
    totalQty,
    totalMenuTypeQty,
    totalAmount,
    finalAmount,

    setHeadcount,
    getCartMenuPrice,
    addCartMenu,
    removeCartMenu,
    initializeCart,
    setQty,
    showQtyModal,
    hideQtyModal,
    getQtyByMenu,
    getCartTotalTaxAmount,
    getCartTotalFreeAmount,
  };
});
